import { ADMIN_PASSWORD } from '../utils/auth';

const API_BASE_URL =
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Set();
  }

  async request(path, options = {}) {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error(
        response.ok ? 'Invalid server response' : `Server error (${response.status}) — restart the API server if you just added new routes`,
      );
    }
    if (!response.ok || !result.success) {
      throw new Error(result.error || `Request failed (${response.status})`);
    }
    return result.data;
  }

  fetchCalls() {
    return this.request('/calls');
  }

  fetchStats() {
    return this.request('/stats');
  }

  fetchSettings() {
    return this.request('/settings');
  }

  saveSettings(settings) {
    return fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }).then((r) => r.json()).then((res) => {
      if (!res.success) throw new Error(res.error);
      return res.data;
    });
  }

  fetchAppointments() {
    return this.request('/appointments');
  }

  fetchSlots(date) {
    return this.request(`/appointments/slots?date=${date}`);
  }

  createAppointment(payload) {
    return this.request('/appointments/direct', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  updateAppointment(id, patch) {
    return this.request(`/appointments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteAppointment(id) {
    return this.request(`/appointments/${id}`, { method: 'DELETE' });
  }

  saveAppointmentCapacity(payload) {
    return this.request('/appointments/capacity', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  saveDaySchedule(payload) {
    return this.request('/appointments/day-schedule', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  testWebhook() {
    return fetch(`${API_BASE_URL}/test-webhook`, { method: 'POST' }).then((r) =>
      r.json()
    );
  }

  logWidgetSession(payload) {
    return fetch(`${API_BASE_URL}/calls/from-widget`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => r.json());
  }

  startRealTimeUpdates() {
    if (this.eventSource) return;
    this.eventSource = new EventSource(`${API_BASE_URL}/events`);
    this.eventSource.onmessage = (event) => {
      try {
        this.notifyListeners(JSON.parse(event.data));
      } catch (e) {
        console.error('SSE parse error', e);
      }
    };
    this.eventSource.onerror = () => {
      setTimeout(() => {
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.stopRealTimeUpdates();
          this.startRealTimeUpdates();
        }
      }, 5000);
    };
  }

  stopRealTimeUpdates() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  addUpdateListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(data) {
    this.listeners.forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error(e);
      }
    });
  }

  async checkHealth() {
    const response = await fetch(`${API_BASE_URL}/health`);
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Health check failed');
    }
    return result;
  }

  async isHealthy() {
    try {
      await this.checkHealth();
      return true;
    } catch {
      return false;
    }
  }

  /** Full health payload for Entegrasyon panel */
  fetchHealth() {
    return this.checkHealth();
  }

  async fetchCallRecording(callId) {
    const response = await fetch(`${API_BASE_URL}/calls/${callId}/recording`, {
      headers: { 'X-Admin-Password': ADMIN_PASSWORD },
    });
    if (!response.ok) {
      let message = 'Ses kaydı yüklenemedi';
      try {
        const result = await response.json();
        message = result.error || message;
      } catch {
        /* binary or empty body */
      }
      throw new Error(message);
    }
    return response.blob();
  }
}

const apiService = new ApiService();
export default apiService;
